/**
 * get(), set(), remove(), clear(), and listen() are based on MV3 extension
 * storage.local functions
 */
export type StorageKeys = null | string | string[];
export type StorageItems = Record<string, any>;
export type StorageChange = Record<string, {newValue?: any; oldValue?: any}>;

export default abstract class Storage {
	abstract get(keys: StorageKeys | StorageItems): Promise<StorageItems>;
	abstract set(items: StorageItems): Promise<void>;
	abstract remove(keys: StorageKeys): Promise<void>;
	abstract clear(): Promise<void>;
	abstract listen(listener: (changes: StorageChange) => void): void;

	// Adds a prefix to the given key(s), to prevent collisions.
	static addKeyPrefix = (key: string, prefix: string): string => prefix + key;
	static addKeyPrefixMultiple(keys: StorageKeys | StorageItems | StorageChange, prefix: string): StorageKeys | StorageItems | StorageChange {
		if (keys === null)
			return null;
		else if (typeof keys === "string")
			return Storage.addKeyPrefix(keys, prefix);
		else if (Array.isArray(keys)) {
			// keys is an array of strings
			return keys.map(key => Storage.addKeyPrefix(key, prefix));
		} else {
			/**
			 * keys is a dictionary of {key: defaultValue} entries
			 * See https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/API/storage/StorageArea/get
			 */
			const result: StorageItems = {};
			Object.entries(keys).forEach(([key, value]) => {
				result[Storage.addKeyPrefix(key, prefix)] = value;
			});
			return result;
		}
	}

	// Removes our prefix (filters out keys without it).
	static removeKeyPrefix = (key: string, prefix: string): string | null => key.startsWith(prefix) ? key.substring(prefix.length) : null;
	static removeKeyPrefixMultiple(keys: StorageKeys | StorageItems | StorageChange, prefix: string): StorageKeys | StorageItems | StorageChange {
		if (keys === null)
			return null;
		else if (typeof keys === "string")
			return Storage.removeKeyPrefix(keys, prefix);
		else if (Array.isArray(keys)) {
			// keys is an array of strings
			return keys
				.map(key => Storage.removeKeyPrefix(key, prefix))
				.filter(key => key !== null);
		} else {
			/**
			 * keys is a dictionary of {key: defaultValue} entries
			 * See https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/API/storage/StorageArea/get
			 */
			const result: StorageItems = {};
			Object.entries(keys).forEach(([key, value]) => {
				const processed = Storage.removeKeyPrefix(key, prefix);
				if (processed !== null)
					result[processed] = value;
			});
			return result;
		}
	}

	async getOne(key: string): Promise<any> {
		const data = await this.get(key);
		return data[key];
	}
	async setOne(key: string, value: any): Promise<void> {
		return this.set({[key]: value});
	}
}
